import { useState } from 'react';

import classNames from 'classnames';

import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { InputTextarea, InputTextareaProps } from 'primereact/inputtextarea';
import { Dropdown, DropdownProps } from 'primereact/dropdown';
import { MultiSelect, MultiSelectProps } from 'primereact/multiselect';
import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete';
import { InputNumber, InputNumberProps } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { Calendar, CalendarProps } from 'primereact/calendar';
import { RadioButton } from 'primereact/radiobutton';

import CkEditor from '../component/CkEditor';

import { translate } from '../language';

import { FormatDisplay } from '../format';

type IamFieldType = '' | 'AutoComplete' | 'Dropdown' | 'MultiSelect' | 'InputNumber' | 'Checkbox' | 'MultiCheckbox' | 'Calendar' | 'RadioButton' | 'Password' | 'InputTextarea' | 'email' | 'CKEditor' | 'file' | 'custom' | 'template' | 'hidden' | 'tax-code' | 'text' | 'username';

interface InputFileProps {
  accept?: string;
}

interface InputTextProps {
  maxLength?: number;
  keyfilter?: any;
  type?: string;
  validator?: string;
  placeholder?: string;
}

interface RadioButtonProps {
  RadioButtons: {
    className?: string;
    value: any;
    label: string;
  }[];
}

interface MultiCheckboxProps {
  Checkboxes: {
    className?: string;
    value: any;
    label: string;
    disabled?: boolean;
    disabledDependency?(item: any): boolean;
  }[];
}

export interface Field {
  field?: string;
  header?: string;
  subHeader?: any;
  type?: IamFieldType;
  CalendarProps?: CalendarProps;
  InputTextProps?: InputTextProps;
  InputTextareaProps?: InputTextareaProps;
  AutoCompleteProps?: AutoCompleteProps;
  InputNumberProps?: InputNumberProps;
  DropdownProps?: DropdownProps;
  MultiSelectProps?: MultiSelectProps;
  InputFileProps?: InputFileProps;
  RadioButtonProps?: RadioButtonProps;
  MultiCheckboxProps?: MultiCheckboxProps;
  required?: boolean | 'onlyCreate';
  className?: string;
  wrapName?: string;
  disabled?: boolean | 'onlyUpdate';
  uppercase?: boolean;
  body?: any;

  displayDependency?(item: any): boolean;

  labelClassName?: string;
  inputClassName?: string;

  template?(item: any): any;
}

interface FormProps {
  fields: Array<Field>;
  disabled?: boolean;
  autoFocus?: boolean;
  idUpdate?: any;

  layout?: string;
  formClassName?: string;
}

export const useForm = (props: FormProps) => {
  const { fields, disabled, autoFocus, idUpdate, layout, formClassName } = props;
  const getEmptyFieldValue = (field: Field) => {
    let value;
    switch (field.type) {
      case 'InputNumber':
        value = null;
        break;
      case 'MultiCheckbox':
        value = [];
        break;
      default:
        value = '';
    }
    return value;
  };
  const emptyItem = {};
  fields.forEach((field) => {
    emptyItem[field.field] = getEmptyFieldValue(field);
  });
  const [item, setItem] = useState(emptyItem);
  const [validators, setValidators] = useState({});
  const [touched, setTouched] = useState(false);

  const reset = () => {
    setValidators({});
    setItem(emptyItem);
    setTouched(false);
  };

  const setValue = (data) => {
    const newItem = {};
    fields.forEach((field) => {
      let _value = data[field.field];
      if (_value === undefined || _value === null) {
        _value = getEmptyFieldValue(field);
      }
      newItem[field.field] = _value;
    });
    setItem(newItem);
  };

  const getValue = () => {
    const value: any = {};
    if (item && Object.keys(item)) {
      Object.keys(item).forEach((key) => {
        switch (typeof item[key]) {
          case 'string':
            if (item[key]) {
              value[key] = item[key].trim();
            }
            break;
          case 'object':
            let type;
            for (const field of fields) {
              if (field.field === key) {
                type = field.type;
              }
            }
            switch (type) {
              case 'Calendar':
                value[key] = FormatDisplay.date(item[key], 'YYYY-MM-DDTHH:mm:ss');
                break;
              // case 'Dropdown':
              //   value[key] = FormatDisplay.date(item[key], 'YYYY-MM-DDTHH:mm:ss');
              //   break;
              default:
                value[key] = item[key];
            }
            break;
          default:
            value[key] = item[key];
        }
      });
    }
    return value;
  };
  const valid = () => {
    let _validators = {},
      valid = true;

    fields.forEach((field) => {
      const fieldValidator = getFieldValidator(field, item[field.field]);
      _validators[field.field] = fieldValidator;
      if (fieldValidator) {
        valid = false;
      }
    });
    setValidators(_validators);

    return valid;
  };

  const validField = (field, fieldValue: any) => {
    const _validators = { ...validators };
    _validators[field.field] = getFieldValidator(field, fieldValue);
    setValidators(_validators);
  };
  const getFieldValidator = (field, fieldValue: any) => {
    let error = '';
    if (field.disabled) {
      return '';
    }
    switch (field.type) {
      case 'InputNumber':
        if ((field.required === true || (field.required === 'onlyCreate' && !idUpdate)) && isNaN(parseFloat(fieldValue))) {
          error = 'required';
        }
        break;
      case 'file':
        if ((field.required === true || (field.required === 'onlyCreate' && !idUpdate)) && !fieldValue) {
          error = 'required';
        }
        break;
      default:
        fieldValue = `${fieldValue}`;
        if (field.InputTextProps && field.InputTextProps.validator === 'taxCode' && !validateTaxCode(fieldValue)) {
          error = 'taxCode';
        } else if (field.required === true && field.type === 'username' && !/^[a-zA-Z0-9]+$/.test(fieldValue)) {
          error = 'pattern';
        } else {
          if ((field.required === true || (field.required === 'onlyCreate' && !idUpdate)) && (!fieldValue || !fieldValue.trim())) {
            error = 'required';
          } else if ((field.required === true || (field.required === 'onlyCreate' && !idUpdate)) && field.type === 'email' && !/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(fieldValue)) {
            error = 'pattern';
          } else if (field.InputTextProps && field.InputTextProps.validator === 'multipleEmail') {
            fieldValue.split(/[;,]\s*/).forEach((email) => {
              if (!/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email.trim())) {
                error = 'pattern';
              }
            });
          }
        }
    }
    return error;
  };

  const validateTaxCode = (value) => {
    let patt = /^[0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9]$/;
    if (value.length > 10) {
      patt = /^[0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][-][0-9][0-9][0-9]$/;
    }
    if (value === '') return true;
    if (patt.test(value) === false) {
      return false;
    }
    const number = Number(value.charAt(0)) * 31 + Number(value.charAt(1)) * 29 + Number(value.charAt(2)) * 23 + Number(value.charAt(3)) * 19 + Number(value.charAt(4)) * 17 + Number(value.charAt(5)) * 13 + Number(value.charAt(6)) * 7 + Number(value.charAt(7)) * 5 + Number(value.charAt(8)) * 3;

    return 10 - (number % 11) === Number(value.charAt(9));
  };

  const onInputChange = (field, value: any) => {
    const _item: any = { ...item };
    if (field.uppercase && value) {
      value = value.toUpperCase();
    }
    _item[field.field] = value;

    _item.inputChange = field.field;

    setItem(_item);
    setTouched(true);
    validField(field, value);
  };
  const onMultiCheckboxChange = (field, e: any) => {
    const selectedItems = [...item[field.field]];
    if (e.checked) selectedItems.push(e.value);
    else selectedItems.splice(selectedItems.indexOf(e.value), 1);

    onInputChange(field, selectedItems);
  };

  const render = () => {
    const dynamicFields = fields.map((field, index) => {
      if (field.displayDependency && field.displayDependency(item) === false) {
        return null;
      } else if (field.type === 'hidden') {
        return null;
      } else if (field.type === 'custom') {
        return (
          <div key={index} className={`field col-12 ${field.className}`}>
            {field.body}
          </div>
        );
      } else if (field.type === 'template') {
        return (
          <div key={index} className={`field col-12 ${field.className}`}>
            {field.template(item)}
          </div>
        );
      } else {
        let required = null;
        if (field.required === true || (field.required === 'onlyCreate' && !idUpdate)) {
          required = <span className="required">*</span>;
        }
        let label;
        if (field.header) {
          let labelClassName;
          if (field.labelClassName) {
            labelClassName = `col-12 mb-2 ${field.labelClassName} md:mb-0`;
          }
          label = (
            <label htmlFor={field.field} className={labelClassName}>
              {translate(field.header)}
              {required}
              {field?.subHeader}
            </label>
          );
        }

        let input;

        const className = classNames({ 'p-invalid': validators[field.field] });
        const fieldDisabled = disabled || field.disabled === true || (field.disabled === 'onlyUpdate' && idUpdate);
        const value = item[field.field];
        const _autoFocus = autoFocus && index === 0;

        switch (field.type) {
          case 'AutoComplete':
            input = <AutoComplete id={field.field} value={value} onChange={(e) => onInputChange(field, e.value)} autoFocus={_autoFocus} className={className} disabled={!!fieldDisabled} {...field.AutoCompleteProps} />;
            break;
          case 'Dropdown':
            input = <Dropdown id={field.field} value={value} onChange={(e) => onInputChange(field, e.value)} autoFocus={_autoFocus} className={className} disabled={!!fieldDisabled} {...field.DropdownProps} />;
            break;
          case 'MultiSelect':
            input = <MultiSelect id={field.field} value={value} onChange={(e) => onInputChange(field, e.value)} autoFocus={_autoFocus} className={className} disabled={!!fieldDisabled} {...field.MultiSelectProps} />;
            break;
          case 'InputNumber':
            input = <InputNumber locale={FormatDisplay.locales} id={field.field} value={value} onChange={(e) => onInputChange(field, e.value)} autoFocus={_autoFocus} className={className} disabled={!!fieldDisabled} {...field.InputNumberProps} />;
            break;
          case 'Checkbox':
            input = <Checkbox inputId={field.field} checked={value} onChange={(e) => onInputChange(field, e.checked)} disabled={!!fieldDisabled} />;
            break;
          case 'Calendar':
            const submit = () => {
              setTimeout(() => {
                document.getElementById('root').click();
              }, 100);
            }
            const current = () => {
              setTimeout(() => {
                onInputChange(field, new Date());
                submit();
              }, 200);
            }
            const footerTemplate = () => {
              return <div className="flex justify-content-between">
                {/*<Button label="Hiện tại" className="p-button-rounded p-button-outlined p-button-secondary" onClick={current}/>*/}
                <div></div>
                <Button label="Nhận" className="p-button-rounded" onClick={submit} />
              </div>
            };
            input = <Calendar id={field.field} value={value ? new Date(value) : value} onChange={(e) => onInputChange(field, e.value)} className={className} dateFormat="dd/mm/yy" disabled={!!fieldDisabled} {...field.CalendarProps}
              footerTemplate={footerTemplate} />;
            break;
          case 'RadioButton':
          case 'tax-code':
          case 'text':
          case 'MultiCheckbox':
            break;
          case 'CKEditor':
            input = <CkEditor value={value} onChange={(e) => onInputChange(field, e.value)} />;
            break;
          case 'file':
            input = <input id={field.field} type="file" onChange={(e) => onInputChange(field, e.target.files[0])} autoFocus={_autoFocus} className={className} disabled={!!fieldDisabled} {...field.InputFileProps} />;
            break;
          case 'InputTextarea':
            input = <InputTextarea id={field.field} value={value} onChange={(e) => onInputChange(field, e.target.value)} autoResize autoFocus={_autoFocus} className={className} disabled={!!fieldDisabled} {...field.InputTextareaProps} />;
            break;
          case 'Password':
            input = <Password id={field.field} value={value} onChange={(e) => onInputChange(field, e.target.value)} toggleMask autoFocus={_autoFocus} className={className} disabled={!!fieldDisabled} feedback={false} autoComplete="off" />;
            break;
          default:
            input = <InputText id={field.field} type="text" value={value} onChange={(e) => onInputChange(field, e.target.value)} autoFocus={_autoFocus} className={className} disabled={!!fieldDisabled} {...field.InputTextProps} autoComplete="one-time-code" />;
        }

        let dynamicField;
        switch (layout) {
          case 'Horizontal':
            switch (field.type) {
              case 'RadioButton':
                dynamicField = (
                  <div key={index} className={`col-12 ${field.className}`}>
                    <div className={`grid ${field.wrapName}`}>
                      {field.RadioButtonProps.RadioButtons.map((radioButton, index) => {
                        return (
                          <div key={index} className={`col-12 ${radioButton.className}`}>
                            <div className="field-radiobutton">
                              <RadioButton inputId={field.field + radioButton.value} name={field.field} value={radioButton.value} onChange={(e) => onInputChange(field, e.value)} checked={value === radioButton.value} />
                              <label htmlFor={field.field + radioButton.value}>{radioButton.label}</label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
                break;
              case 'MultiCheckbox':
                dynamicField = (
                  <div key={index} className={`col-12 ${field.className}`}>
                    {field.MultiCheckboxProps.Checkboxes.map((checkbox, index) => {
                      let _disabled = checkbox.disabled;
                      if (checkbox.disabledDependency) {
                        _disabled = checkbox.disabledDependency(item);
                      }
                      return (
                        <div key={index} className="field-checkbox">
                          <Checkbox inputId={field.field + checkbox.value} name={field.field} value={checkbox.value} disabled={_disabled === true} onChange={(e) => onMultiCheckboxChange(field, e)} checked={value.indexOf(checkbox.value) !== -1} />
                          <label htmlFor={field.field + checkbox.value}>{checkbox.label}</label>
                        </div>
                      );
                    })}
                  </div>
                );
                break;
              case 'tax-code':
                let _value;
                if (value) {
                  const codes = value.replace('-', '');
                  _value = codes.split('').map((code, index) => {
                    return <div key={index}>{code}</div>;
                  });
                }
                dynamicField = (
                  <div key={index} className={`col-12 ${field.className}`}>
                    <div className="field grid">
                      {label}
                      <div className={`col-12 tax-code ${field.inputClassName}`}>{_value}</div>
                    </div>
                  </div>
                );
                break;
              case 'text':
                dynamicField = (
                  <div key={index} className={`col-12 ${field.className}`}>
                    <div className="field grid">
                      {label}
                      <div className={`col-12 ${field.inputClassName}`}>{value}</div>
                    </div>
                  </div>
                );
                break;
              default:
                dynamicField = (
                  <div key={index} className={`col-12 ${field.className}`}>
                    <div className="field grid">
                      {label}
                      <div className={`col-12 ${field.inputClassName}`}>
                        {input}
                        {validators[field.field] === 'required' && <small className="p-error block">{translate(field.header)} không được bỏ trống.</small>}
                        {validators[field.field] === 'pattern' && <small className="p-error block">{translate(field.header)} không hợp lệ.</small>}
                        {validators[field.field] === 'taxCode' && <small className="p-error block">Mã số thuế không hợp lệ</small>}
                      </div>
                    </div>
                  </div>
                );
            }

            break;
          default:
            switch (field.type) {
              case 'Checkbox':
                dynamicField = (
                  <div key={index} className={`field col-12 ${field.className}`}>
                    <div className="field-checkbox">
                      {input}
                      {label}
                    </div>
                  </div>
                );
                break;
              default:
                dynamicField = (
                  <div key={index} className={`field col-12 ${field.className}`}>
                    {label}
                    {input}
                    {validators[field.field] === 'required' && <small className="p-error block">{translate(field.header)} không được bỏ trống.</small>}
                    {validators[field.field] === 'pattern' && <small className="p-error block">{translate(field.header)} không hợp lệ.</small>}
                    {validators[field.field] === 'taxCode' && <small className="p-error block">Mã số thuế không hợp lệ</small>}
                  </div>
                );
            }
        }
        return dynamicField;
      }
    });

    return <div className={`p-fluid formgrid grid ${formClassName}`}>{dynamicFields}</div>;
  };

  return { render, getValue, reset, setValue, valid, touched, setTouched };
};

export const useCalendarFooterTemplate = ({ applyCallback }) => {
  const submit = () => {
    setTimeout(() => {
      document.getElementById('root').click();
    }, 100);
  }
  const current = () => {
    setTimeout(() => {
      applyCallback(new Date());
      submit();
    }, 200);
  }
  let render = () => {
    return <div className="flex justify-content-between">
      {/*<Button label="Hiện tại" className="p-button-rounded p-button-outlined p-button-secondary" onClick={current}/>*/}
      <div></div>
      <Button label="Nhận" className="p-button-rounded" onClick={submit} />
    </div>
  };

  return { render };
}